import React, { useEffect, useMemo, useState } from 'react';
import { Container, Row, Col, CardBody, Card } from 'reactstrap';
import Badge from 'react-bootstrap/Badge';
import PndRestApi from 'src/utils/pnd/PndRestApi';
import { LinearProgress, SnackbarContent, Box, Button } from '@mui/material';
import CancelPresentationRoundedIcon from '@mui/icons-material/CancelPresentationRounded';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import ConfirmationDialog from 'src/shared/components/ConfirmationDialog';
import { showNotification, successNofication } from 'src/shared/components/Notification';
import { Redirect } from 'react-router-dom';
import PageHeader from 'src/shared/components/layout/PageHeader';
import RadioSelectTable from 'src/shared/components/table/RadioSelectTable';

import {
  OPERATION_TYPE_FOR_CANCELLED,
  ORDER_CANCELLATION_REQUEST_STATUS,
  HUB_MANAGER_ROLE_ID,
  ERROR_MESSAGES
} from 'src/utils/pnd/PndConstants';

const OrderCancellationDetailsIndex = () => {
  const [reactTableData, setReactTableData] = useState({ tableRowsData: [] });
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isNoRecordFound, setNoRecordFound] = useState(false);
  const [isRedirect, setRedirect] = useState(false);
  const [isOrderId, setOrderId] = useState(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [isOperationType, setOperationType] = useState(null);

  useEffect(() => {
    getAllOrderDetails();
  }, []);

  const getAllOrderDetails = async () => {
    try {
      setLoading(true);
      setNoRecordFound(false);
      let Obj = {};
      Obj["operationType"] = OPERATION_TYPE_FOR_CANCELLED;
      Obj["orderType"] = OPERATION_TYPE_FOR_CANCELLED;
      Obj["isCancellation"] = true;

      const response = await PndRestApi.GetAllOrders(Obj);
      if (response.status === 200 && response.data.status === 200) {
        const list = response.data?.data || [];
        setReactTableData({ tableRowsData: list });
        if (list.length > 0) {
          setSelectedOrder(list[0]);
        } else {
          setSelectedOrder(null);
        }
      } else if (response?.data?.status && response?.data?.status === 204) {
        setNoRecordFound(true);
        setReactTableData({ tableRowsData: [] });
        setSelectedOrder(null);
      } else {
        showNotification(
          "theme-light",
          "ltr",
          `SuccessCode: ${response.data.status}`,
          `Message:${response.data.message}`,
        );
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      showNotification(
        "theme-light",
        "ltr",
        `SuccessCode: 500`,
        ERROR_MESSAGES.tryCatchError,
      );
    } finally {
      setLoading(false);
    }
  };

  const closeDialog = () => {
    setShowConfirmation(false);
  };

  const showDialogBox = async (order, operationType) => {
    setOperationType(operationType);
    setShowConfirmation(true);
    if (order?.orderId) {
      setOrderId(order?.orderId);
    }
  };

  const processOrder = async () => {
    if (isOrderId && isOperationType === OPERATION_TYPE_FOR_CANCELLED) {
      OrderStatusUpdate();
    }
  };

  const OrderStatusUpdate = async () => {
    try {
      setLoading(true);
      setShowConfirmation(false);
      let Obj = {};
      Obj["operationType"] = isOperationType;
      Obj["orderId"] = isOrderId;
      Obj["riderId"] = null;

      const response = await PndRestApi.UpdateOrderStatusDetails(Obj);
      if (response.status === 200 && response.data.status === 200) {
        successNofication(
          "theme-light",
          "ltr",
          `Message: Order Status Updated Successfully`
        );
        setRedirect(true);
      } else {
        showNotification(
          "theme-light",
          "ltr",
          `SuccessCode: ${response.data.status}`,
          `Message:${response.data.message}`,
        );
      }
    } catch (error) {
      console.error('Error processing status update:', error);
      showNotification(
        "theme-light",
        "ltr",
        `SuccessCode: 500`,
        ERROR_MESSAGES.tryCatchError,
      );
    } finally {
      setLoading(false);
    }
  };

  const ORDER_COLUMNS = useMemo(() => [
    {
      key: 'orderNumber',
      header: 'Order No.',
      sortable: true,
    },
    {
      key: 'erpOrderNo',
      header: 'ERP Order No.',
      sortable: true,
    },
    // {
    //   key: 'orderType',
    //   header: 'Type',
    //   sortable: true,
    //   render: (value, row) => (
    //     <>
    //       {row.orderType === 1 ? (
    //         <Badge bg="success">DO</Badge>
    //       ) : row.orderType === 2 ? (
    //         <Badge bg="primary">PU</Badge>
    //       ) : (
    //         <Badge bg="warning">PnD</Badge>
    //       )}
    //     </>
    //   )
    // },
    // {
    //   key: 'fromName',
    //   header: 'From Customer',
    //   sortable: true,
    // },
    // {
    //   key: 'toName',
    //   header: 'To Customer',
    //   sortable: true,
    // },
    {
      key: 'orderStatus',
      header: 'Status',
      sortable: true,
    }
  ], []);

  const getFromTypeLabel = (fromTypeId) => {
    switch (fromTypeId) {
      case 1: return 'Own Hub';
      case 2: return 'Hub Customer';
      case 3: return 'Merchant';
      case 4: return 'Another Hub';
      default: return 'Merchant Customer';
    }
  };

  const getToTypeLabel = (toTypeId) => {
    switch (toTypeId) {
      case 1: return 'Own Hub';
      case 2: return 'Hub Customer';
      case 3: return 'Merchant';
      case 4: return 'Another Hub';
      default: return 'Merchant Customer';
    }
  };

  if (isRedirect) {
    return <Redirect to={`/web_app/views/order/cancelled_order_view`} />;
  }

  return (
    <Box sx={{ p: 3, backgroundColor: '#f8fafc', minHeight: '100vh' }}>
      <Container fluid style={{ maxWidth: '1400px' }}>
        <PageHeader
          icon={<CancelPresentationRoundedIcon fontSize="large" />}
          title="Order Cancellation Details"
          subtitle="Manage active order cancellations and request approvals."
          gradient="linear-gradient(135deg, #ef4444 0%, #b91c1c 100%)"
        />

        {loading && <LinearProgress sx={{ mb: 2, borderRadius: 2 }} />}

        <Row style={{ marginTop: '20px' }}>
          {/* Left Panel: Master List */}
          <Col md={7} lg={7}>
            {!isNoRecordFound ? (
              <RadioSelectTable
                rows={reactTableData.tableRowsData || []}
                columns={ORDER_COLUMNS}
                rowKey="orderId"
                selectedKey={selectedOrder?.orderId}
                onSelect={setSelectedOrder}
                searchKeys={['orderNumber', 'erpOrderNo', 'erpNo', 'fromName', 'toName', 'orderStatus']}
                searchPlaceholder="Search order cancellations..."
                pageSizes={[5, 10, 20]}
              />
            ) : (
              <Card style={{ borderRadius: '12px', border: '1px solid #e2e8f0', padding: '40px', textAlign: 'center' }}>
                <p className="text-muted m-0">No cancellation records found.</p>
              </Card>
            )}
          </Col>

          {/* Right Panel: Sleek Detail Card */}
          <Col md={5} lg={5}>
            <Card className="card--not-full-height" style={{
              borderRadius: '12px',
              border: '1px solid #e2e8f0',
              boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)',
              minHeight: '520px',
              background: '#ffffff',
              overflow: 'hidden',
            }}>
              {selectedOrder ? (
                <>
                  {/* Header Banner */}
                  <div style={{
                    background: 'linear-gradient(135deg, #ef4444 0%, #b91c1c 100%)',
                    padding: '24px 20px',
                    color: '#ffffff'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div>
                        <h4 style={{ margin: 0, fontWeight: 800, fontSize: '1.2rem', color: '#ffffff' }}>
                          {selectedOrder.orderNumber}
                        </h4>
                        <span style={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.85)' }}>
                          Status: {selectedOrder.orderStatus || '—'}
                        </span>
                      </div>
                      <div>
                        {selectedOrder.orderCancellationStatus === ORDER_CANCELLATION_REQUEST_STATUS ? (
                          <span className="badge bg-warning text-dark" style={{ fontSize: '0.75rem', fontWeight: 700, padding: '5px 10px', borderRadius: '12px' }}>
                            REQUESTED
                          </span>
                        ) : selectedOrder.orderCancellationStatus != null ? (
                          <span className="badge bg-light text-dark" style={{ fontSize: '0.75rem', fontWeight: 700, padding: '5px 10px', borderRadius: '12px' }}>
                            {selectedOrder.orderCancellationStatus}
                          </span>
                        ) : null}
                      </div>
                    </div>
                  </div>

                  {/* Details Body */}
                  <CardBody style={{ padding: '20px' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>

                      {/* General Info */}
                      <div>
                        <h6 className="text-muted text-uppercase fw-bold mb-2" style={{ fontSize: '0.7rem', letterSpacing: '0.5px' }}>
                          General Info
                        </h6>
                        <div style={{ background: '#f8fafc', borderRadius: '8px', padding: '12px 14px', border: '1px solid #f1f5f9', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                          <div className="d-flex justify-content-between">
                            <span className="text-muted small">Order Type:</span>
                            <span className="fw-bold small text-dark">
                              {selectedOrder.orderType === 1 ? (
                                <Badge bg="success">DO</Badge>
                              ) : selectedOrder.orderType === 2 ? (
                                <Badge bg="primary">PU</Badge>
                              ) : (
                                <Badge bg="warning">PnD</Badge>
                              )}
                            </span>
                          </div>
                          {selectedOrder.orderCancellationRequestedFrom && (
                            <div className="d-flex justify-content-between">
                              <span className="text-muted small">Requested From:</span>
                              <span className="fw-bold small">
                                {selectedOrder.orderCancellationRequestedFrom === 5 ? (
                                  <Badge bg="warning">Merchant</Badge>
                                ) : selectedOrder.orderCancellationRequestedFrom === 1 ? (
                                  <Badge bg="warning">Own Hub</Badge>
                                ) : (
                                  <Badge bg="light" text="dark">Other</Badge>
                                )}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Client Routing */}
                      <div>
                        <h6 className="text-muted text-uppercase fw-bold mb-2" style={{ fontSize: '0.7rem', letterSpacing: '0.5px' }}>
                          Route Details
                        </h6>
                        <div style={{ background: '#f8fafc', borderRadius: '8px', padding: '12px 14px', border: '1px solid #f1f5f9', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                          <div>
                            <div className="d-flex justify-content-between mb-1">
                              <span className="text-muted small fw-bold">From:</span>
                              <span className="badge bg-secondary" style={{ fontSize: '0.68rem' }}>
                                {getFromTypeLabel(selectedOrder.fromTypeId)}
                              </span>
                            </div>
                            <span className="fw-semibold small text-dark d-block">{selectedOrder.fromName || '—'}</span>
                          </div>
                          <hr style={{ margin: '8px 0', borderColor: '#e2e8f0' }} />
                          <div>
                            <div className="d-flex justify-content-between mb-1">
                              <span className="text-muted small fw-bold">To:</span>
                              <span className="badge bg-secondary" style={{ fontSize: '0.68rem' }}>
                                {getToTypeLabel(selectedOrder.toTypeId)}
                              </span>
                            </div>
                            <span className="fw-semibold small text-dark d-block">{selectedOrder.toName || '—'}</span>
                          </div>
                        </div>
                      </div>

                      {/* Action Button Section */}
                      <div className="mt-auto pt-3">
                        {selectedOrder.orderCancellationStatus === ORDER_CANCELLATION_REQUEST_STATUS && localStorage.getItem("uri") === HUB_MANAGER_ROLE_ID ? (
                          <Button
                            variant="contained"
                            color="warning"
                            fullWidth
                            startIcon={<CheckBoxIcon />}
                            onClick={() => showDialogBox(selectedOrder, OPERATION_TYPE_FOR_CANCELLED)}
                            sx={{
                              borderRadius: '8px',
                              textTransform: 'none',
                              fontWeight: 600,
                              py: '10px',
                            }}
                          >
                            Approve Cancellation Request
                          </Button>
                        ) : selectedOrder.orderCancellationStatus == null ? (
                          <Button
                            variant="contained"
                            color="error"
                            fullWidth
                            startIcon={<CancelPresentationRoundedIcon />}
                            onClick={() => showDialogBox(selectedOrder, OPERATION_TYPE_FOR_CANCELLED)}
                            sx={{
                              borderRadius: '8px',
                              textTransform: 'none',
                              fontWeight: 600,
                              py: '10px',
                            }}
                          >
                            Cancel Order Details
                          </Button>
                        ) : (
                          <Button
                            variant="contained"
                            disabled
                            fullWidth
                            sx={{
                              borderRadius: '8px',
                              textTransform: 'none',
                              fontWeight: 600,
                              py: '10px',
                            }}
                          >
                            Requested
                          </Button>
                        )}
                      </div>

                    </div>
                  </CardBody>
                </>
              ) : (
                <div style={{
                  display: 'flex', flexDirection: 'column', alignItems: 'center',
                  justifyContent: 'center', padding: '40px', textAlign: 'center', minHeight: '520px',
                }}>
                  <div style={{ fontSize: '3.5rem', color: '#cbd5e1', marginBottom: '16px' }}>📦</div>
                  <h5 className="fw-bold text-muted">No Order Selected</h5>
                  <p className="text-muted small">Select an order from the list to view cancellation details.</p>
                </div>
              )}
            </Card>
          </Col>
        </Row>
      </Container>

      {isNoRecordFound && <SnackbarContent message="No Record Found" sx={{ mt: 2 }} />}

      {showConfirmation && (
        <ConfirmationDialog
          open={showConfirmation}
          onClose={closeDialog}
          onConfirm={processOrder}
          title="Confirmation"
          message="Are you sure you want to Cancel the Order?"
        />
      )}
    </Box>
  );
};

export default OrderCancellationDetailsIndex;